import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../components/ui/tooltip';
import { Progress } from '../../components/ui/progress';
import { Plus, Trash2, Eye, Download, AlertCircle, X, Loader2, Search, Upload } from 'lucide-react';
import { getFileTypeInfo, formatFileSize } from '../../utils/fileTypeIcon';
import { getPreviewStatus } from '../../utils/previewPolicy';
import { useDownload, formatBytes } from '../../context/DownloadContext';
import PreviewModal from './PreviewModal';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { OrangeCheckbox } from './WizardComponents';

const DEFAULT_DATA_TYPES = ['O&M', 'Warranty', 'Datasheet'];

/**
 * Reusable, self-contained CSI-item Submittals section (extracted from the
 * Electrical Submittals tab so it can be shared across divisions).
 *
 * The submittal API is generic (scoped by projectId + csiItemId), so this single
 * component serves any division/item and keeps data isolated per CSI item.
 *
 * Props:
 *  - projectId, csiItem, canEditCSIItems, canManageProject, onItemCount (data wiring)
 *  - dataTypes: string[] of Data Type options (default O&M/Warranty/Datasheet)
 *  - testIdPrefix: data-testid namespace (default 'elec')
 *  - moduleLabel: human label used in the add-modal description (default 'Electrical Systems')
 */
export const CsiItemSubmittalsSection = ({
  projectId, csiItem, canEditCSIItems, canManageProject,
  onItemCount,
  dataTypes = DEFAULT_DATA_TYPES,
  testIdPrefix = 'elec',
  moduleLabel = 'Electrical Systems',
}) => {
  const [submittals, setSubmittals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [confirmState, setConfirmState] = useState({ open: false });

  // Add submittal modal
  const [showAddModal, setShowAddModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [formTitle, setFormTitle] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formDataType, setFormDataType] = useState('');
  const [formFile, setFormFile] = useState(null);
  const fileInputRef = useRef(null);

  // Search & filters
  const [searchTerm, setSearchTerm] = useState('');
  const [dataTypeFilter, setDataTypeFilter] = useState('');

  // Preview
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState({ url: '', name: '' });
  const [previewLoading, setPreviewLoading] = useState(null);

  const { startDownload, getState } = useDownload();

  // Keep latest onItemCount in a ref so we can notify the parent of the item
  // count without making it a fetch dependency (avoids refetch loops).
  const onItemCountRef = useRef(onItemCount);
  useEffect(() => { onItemCountRef.current = onItemCount; }, [onItemCount]);

  const csiItemId = csiItem?.id;

  // ── Fetch ──
  const fetchSubmittals = useCallback(async () => {
    if (!projectId || !csiItemId) return;
    try {
      const res = await axios.get(`${API}/projects/${projectId}/csi-items/${csiItemId}/submittals`);
      setSubmittals(res.data);
      setSelectedIds(new Set());
      onItemCountRef.current?.(res.data.length);
    } catch {
      toast.error('Failed to load submittals');
    } finally {
      setLoading(false);
    }
  }, [projectId, csiItemId]);

  useEffect(() => { fetchSubmittals(); }, [fetchSubmittals]);

  // ── Filter + Search ──
  const filtered = useMemo(() => {
    let result = submittals;
    if (dataTypeFilter) result = result.filter(s => s.dataType === dataTypeFilter);
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(s =>
        (s.title || '').toLowerCase().includes(term) ||
        (s.filename || '').toLowerCase().includes(term) ||
        (`${s.uploaderFirstName || ''} ${s.uploaderLastName || ''}`).toLowerCase().includes(term)
      );
    }
    return result;
  }, [submittals, dataTypeFilter, searchTerm]);

  const hasFilters = dataTypeFilter || searchTerm;
  const clearFilters = () => { setDataTypeFilter(''); setSearchTerm(''); };

  // ── Selection ──
  const toggleSelectAll = () => {
    if (selectedIds.size === filtered.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(filtered.map(s => s.id)));
  };
  const toggleSelectRow = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const allSelected = filtered.length > 0 && selectedIds.size === filtered.length;

  // ── Create ──
  const handleCreate = async (e) => {
    e.preventDefault();
    if (!formFile) { toast.error('File is required'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('title', formTitle);
      if (formDescription) fd.append('description', formDescription);
      if (formDataType) fd.append('data_type', formDataType);
      fd.append('file', formFile);
      await axios.post(`${API}/projects/${projectId}/csi-items/${csiItemId}/submittals`, fd);
      toast.success('Submittal added');
      resetForm();
      setShowAddModal(false);
      fetchSubmittals();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add submittal');
    } finally {
      setUploading(false);
    }
  };

  const resetForm = () => {
    setFormTitle(''); setFormDescription(''); setFormDataType(''); setFormFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // ── Delete ──
  const handleDelete = (item) => {
    setConfirmState({
      open: true,
      title: 'Delete Submittal',
      description: `Are you sure you want to delete "${item.title}"? This action cannot be undone.`,
      confirmText: 'Delete',
      variant: 'destructive',
      onConfirm: async () => {
        try {
          await axios.delete(`${API}/projects/${projectId}/csi-items/${csiItemId}/submittals/${item.id}`);
          setSubmittals(prev => prev.filter(s => s.id !== item.id));
          setSelectedIds(prev => { const n = new Set(prev); n.delete(item.id); return n; });
          onItemCountRef.current?.(Math.max(0, submittals.length - 1));
          toast.success('Submittal deleted');
        } catch {
          toast.error('Failed to delete submittal');
        }
        setConfirmState({ open: false });
      },
    });
  };

  const handleBulkDelete = () => {
    if (selectedIds.size === 0) return;
    setConfirmState({
      open: true,
      title: `Delete ${selectedIds.size} submittal(s)?`,
      description: `This action cannot be undone.`,
      confirmText: 'Delete',
      variant: 'destructive',
      onConfirm: async () => {
        setBulkDeleting(true);
        try {
          await axios.post(`${API}/submittals/bulk-delete/${projectId}`, { ids: Array.from(selectedIds) });
          setSubmittals(prev => prev.filter(s => !selectedIds.has(s.id)));
          onItemCountRef.current?.(Math.max(0, submittals.length - selectedIds.size));
          setSelectedIds(new Set());
          toast.success(`Deleted ${selectedIds.size} submittal(s)`);
        } catch {
          toast.error('Failed to delete submittals');
        } finally {
          setBulkDeleting(false);
        }
        setConfirmState({ open: false });
      },
    });
  };

  // ── Preview ──
  const openPreview = async (submittal) => {
    setPreviewLoading(submittal.id);
    try {
      if (submittal.source === 'Equipment') {
        const fileUrl = `${API}${submittal.fileUrl}`;
        setPreviewFile({ url: fileUrl, name: submittal.filename });
        setPreviewOpen(true);
      } else {
        const resp = await axios.get(`${API}/submittals/${submittal.id}/preview-url`);
        setPreviewFile({ url: resp.data.url, name: submittal.filename });
        setPreviewOpen(true);
      }
    } catch {
      const fileUrl = submittal.fileUrl?.startsWith('http')
        ? submittal.fileUrl
        : submittal.fileUrl ? `${API}${submittal.fileUrl}` : null;
      if (fileUrl) {
        setPreviewFile({ url: fileUrl, name: submittal.filename });
        setPreviewOpen(true);
      }
    } finally {
      setPreviewLoading(null);
    }
  };

  // ── Download ──
  const handleSecureDownload = (submittal) => {
    if (submittal.source === 'Equipment') {
      const url = `${API}${submittal.fileUrl}`;
      startDownload(submittal.id, async () => url, submittal.filename || 'document', submittal.fileSize || 0);
      return;
    }
    const getUrl = async () => {
      const resp = await axios.get(`${API}/submittals/${submittal.id}/download`);
      return resp.data?.url;
    };
    startDownload(submittal.id, getUrl, submittal.filename || 'document', submittal.fileSize || 0);
  };

  // ── Loading ──
  if (loading) {
    return (
      <div className="flex justify-center py-16" data-testid={`${testIdPrefix}-submittals-loading`}>
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div data-testid={`${testIdPrefix}-submittals-section`}>
      {/* Search + Filter bar */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="relative w-56">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search submittals..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-8 h-8 text-xs"
            data-testid={`${testIdPrefix}-submittals-search`}
          />
        </div>
        <Select value={dataTypeFilter} onValueChange={(val) => setDataTypeFilter(val === '__all__' ? '' : val)}>
          <SelectTrigger className="w-36 h-8 text-xs" data-testid={`${testIdPrefix}-submittals-filter-type`}>
            <SelectValue placeholder="All Data Types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All Data Types</SelectItem>
            {dataTypes.map(dt => <SelectItem key={dt} value={dt}>{dt}</SelectItem>)}
          </SelectContent>
        </Select>
        {hasFilters && (
          <Button variant="ghost" size="sm" className="h-8 text-xs text-slate-500" onClick={clearFilters} data-testid={`${testIdPrefix}-submittals-clear-filters`}>
            <X className="h-3.5 w-3.5 mr-1" />Clear
          </Button>
        )}
      </div>

      {/* Toolbar */}
      <div className="flex justify-end items-center gap-2 mb-6">
        {canManageProject && submittals.length > 0 && (
          <>
            <Button variant="outline" size="sm" onClick={toggleSelectAll} data-testid={`${testIdPrefix}-submittals-select-all`}>
              {allSelected ? 'Deselect All' : 'Select All'}
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={handleBulkDelete}
              disabled={bulkDeleting || selectedIds.size === 0}
              data-testid={`${testIdPrefix}-submittals-bulk-delete`}
            >
              {bulkDeleting ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Trash2 className="h-4 w-4 mr-1.5" />}
              Delete Selected{selectedIds.size > 0 ? ` (${selectedIds.size})` : ''}
            </Button>
          </>
        )}
        {canEditCSIItems && (
          <Button
            size="sm"
            onClick={() => setShowAddModal(true)}
            disabled={submittals.length >= 20}
            data-testid={`${testIdPrefix}-submittals-add-btn`}
          >
            <Plus className="h-4 w-4 mr-1.5" />
            Add Submittal
          </Button>
        )}
      </div>

      {/* Card Grid */}
      {filtered.length === 0 ? (
        <div className="text-center py-12 border-2 border-dashed rounded-lg" data-testid={`${testIdPrefix}-submittals-empty`}>
          <AlertCircle className="h-12 w-12 mx-auto text-slate-300 mb-3" />
          <h3 className="text-lg font-semibold text-slate-900 mb-2">
            {submittals.length === 0 ? 'No Submittals Yet' : 'No submittals match the current filters'}
          </h3>
          <p className="text-sm text-slate-500 mb-4">
            {submittals.length === 0 ? 'Upload your first submittal document to get started' : 'Try adjusting your search or filters'}
          </p>
          {canEditCSIItems && submittals.length === 0 && (
            <Button onClick={() => setShowAddModal(true)} data-testid={`${testIdPrefix}-submittals-empty-add`}>
              <Plus className="h-4 w-4 mr-2" />Add Submittal
            </Button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid={`${testIdPrefix}-submittals-grid`}>
          {filtered.map((submittal) => {
            const fileInfo = getFileTypeInfo(submittal.filename);
            const FileIcon = fileInfo.icon;
            const isSelected = selectedIds.has(submittal.id);
            const hasFile = !!(submittal.fileUrl || (submittal.cloudinary && submittal.cloudinary.public_id));
            const preview = getPreviewStatus(submittal);
            const dl = getState(submittal.id);
            const isDownloading = dl.status === 'downloading';
            const isDone = dl.status === 'done';

            return (
              <Card
                key={submittal.id}
                className={`group relative transition-all hover:shadow-md ${isSelected ? 'ring-2 ring-orange-500 bg-orange-50/30' : ''}`}
                data-testid={`${testIdPrefix}-submittal-card-${submittal.id}`}
              >
                <CardContent className="p-4">
                  {canManageProject && submittal.source !== 'Equipment' && (
                    <div className="absolute top-3 left-3 z-10">
                      <OrangeCheckbox
                        id={`${testIdPrefix}-sub-${submittal.id}`}
                        checked={isSelected}
                        onChange={() => toggleSelectRow(submittal.id)}
                        testId={`${testIdPrefix}-submittal-check-${submittal.id}`}
                      />
                    </div>
                  )}

                  {/* File icon area */}
                  <div className={`flex items-center justify-center w-full h-24 rounded-lg mb-3 ${fileInfo.bg}`}>
                    <div className="flex flex-col items-center gap-1">
                      <FileIcon className={`h-10 w-10 ${fileInfo.color}`} />
                      <span className={`text-xs font-bold uppercase ${fileInfo.color}`}>{fileInfo.label}</span>
                    </div>
                  </div>

                  {/* Title */}
                  <h3 className="font-semibold text-sm text-slate-900 truncate mb-1" title={submittal.title} data-testid={`${testIdPrefix}-submittal-title-${submittal.id}`}>
                    {submittal.title}
                  </h3>

                  {/* Data Type badge */}
                  {submittal.dataType && (
                    <span className="inline-block text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 border border-slate-200 mb-1.5 mr-1">
                      {submittal.dataType}
                    </span>
                  )}

                  {/* Source badge */}
                  {submittal.source === 'Equipment' && (
                    <span className="inline-block text-[10px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-600 border border-blue-200 mb-1.5">
                      Equipment{submittal.sourceTag ? `: ${submittal.sourceTag}` : ''}
                    </span>
                  )}

                  {/* Description */}
                  {submittal.description && (
                    <p className="text-xs text-slate-500 truncate mb-2" title={submittal.description}>
                      {submittal.description}
                    </p>
                  )}

                  {/* File info */}
                  <div className="text-xs text-slate-400 truncate mb-1" title={submittal.filename}>
                    {submittal.filename || 'No file'}
                  </div>
                  {submittal.fileSize && (
                    <div className="text-xs text-slate-400 mb-1">{formatFileSize(submittal.fileSize)}</div>
                  )}

                  {/* Uploader + date */}
                  <div className="flex items-center justify-between text-xs text-slate-500 mb-3">
                    <span className="truncate mr-2" title={`${submittal.uploaderFirstName || ''} ${submittal.uploaderLastName || ''}`.trim()}>
                      {(submittal.uploaderFirstName || submittal.uploaderLastName)
                        ? `${submittal.uploaderFirstName || ''} ${submittal.uploaderLastName || ''}`.trim()
                        : ''}
                    </span>
                    <span className="shrink-0 text-slate-400">{new Date(submittal.createdAt).toLocaleDateString()}</span>
                  </div>

                  {/* Download progress */}
                  {isDownloading && (
                    <div className="mb-2" data-testid={`${testIdPrefix}-submittal-progress-${submittal.id}`}>
                      <Progress value={dl.total ? dl.progress : undefined} className="h-1.5 mb-1" />
                      <div className="flex items-center justify-between text-[10px] text-slate-500">
                        <span>
                          {dl.total
                            ? `${dl.progress}% — ${formatBytes(dl.downloaded)} / ${formatBytes(dl.total)}`
                            : `Downloading… ${formatBytes(dl.downloaded)}`}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Action buttons */}
                  <div className="flex gap-2">
                    {hasFile && (
                      <>
                        <TooltipProvider delayDuration={200}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="flex-1">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="w-full h-8 text-xs"
                                  onClick={() => preview.previewable && openPreview(submittal)}
                                  disabled={!preview.previewable || previewLoading === submittal.id}
                                  data-testid={`${testIdPrefix}-submittal-view-${submittal.id}`}
                                >
                                  <Eye className="h-3.5 w-3.5 mr-1" />
                                  {previewLoading === submittal.id ? 'Loading...' : 'Preview'}
                                </Button>
                              </span>
                            </TooltipTrigger>
                            {!preview.previewable && preview.reason && (
                              <TooltipContent side="top" className="max-w-xs text-xs">
                                {preview.reason}
                              </TooltipContent>
                            )}
                          </Tooltip>
                        </TooltipProvider>
                        <Button
                          variant="outline"
                          size="sm"
                          className={`flex-1 h-8 text-xs transition-colors ${isDone ? '!border-green-500 !text-green-600 !bg-green-50' : ''}`}
                          disabled={isDownloading}
                          onClick={() => handleSecureDownload(submittal)}
                          data-testid={`${testIdPrefix}-submittal-download-${submittal.id}`}
                        >
                          {isDownloading ? (
                            <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />{dl.total ? `${dl.progress}%` : 'Downloading…'}</>
                          ) : isDone ? (
                            <><Download className="h-3.5 w-3.5 mr-1" />Downloaded</>
                          ) : (
                            <><Download className="h-3.5 w-3.5 mr-1" />Download</>
                          )}
                        </Button>
                      </>
                    )}
                    {canEditCSIItems && submittal.source !== 'Equipment' && (
                      <Button
                        variant="destructive"
                        size="sm"
                        className="h-8 text-xs px-2"
                        onClick={() => handleDelete(submittal)}
                        data-testid={`${testIdPrefix}-submittal-delete-${submittal.id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Add Submittal Modal */}
      <Dialog open={showAddModal} onOpenChange={(open) => { if (!open) { resetForm(); setShowAddModal(false); } }}>
        <DialogContent className="max-w-lg" data-testid={`${testIdPrefix}-add-submittal-modal`}>
          <DialogHeader>
            <DialogTitle>Add New Submittal</DialogTitle>
            <DialogDescription>
              Upload a submittal document for {moduleLabel}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor={`${testIdPrefix}-sub-title`}>Title *</Label>
              <Input
                id={`${testIdPrefix}-sub-title`}
                data-testid={`${testIdPrefix}-sub-title-input`}
                value={formTitle}
                onChange={(e) => setFormTitle(e.target.value)}
                placeholder="e.g., Panel Schedule Specs"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor={`${testIdPrefix}-sub-data-type`}>Data Type</Label>
              <Select value={formDataType} onValueChange={setFormDataType}>
                <SelectTrigger id={`${testIdPrefix}-sub-data-type`} data-testid={`${testIdPrefix}-sub-data-type-select`}>
                  <SelectValue placeholder="Select data type" />
                </SelectTrigger>
                <SelectContent>
                  {dataTypes.map(dt => <SelectItem key={dt} value={dt}>{dt}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor={`${testIdPrefix}-sub-description`}>Description</Label>
              <Input
                id={`${testIdPrefix}-sub-description`}
                data-testid={`${testIdPrefix}-sub-description-input`}
                value={formDescription}
                onChange={(e) => setFormDescription(e.target.value)}
                placeholder="Optional description"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor={`${testIdPrefix}-sub-file`}>File *</Label>
              <div className="relative">
                <Input
                  id={`${testIdPrefix}-sub-file`}
                  ref={fileInputRef}
                  data-testid={`${testIdPrefix}-sub-file-input`}
                  type="file"
                  onChange={(e) => setFormFile(e.target.files[0])}
                  required
                />
              </div>
              {formFile && (
                <p className="text-xs text-slate-500">{formFile.name} — {formatFileSize(formFile.size)}</p>
              )}
              {!formFile && (
                <p className="text-xs text-slate-400">PDF, images, documents supported (max 20 submittals per item)</p>
              )}
            </div>
            <div className="flex gap-2">
              <Button
                type="submit"
                disabled={uploading || !formFile}
                className="flex-1"
                data-testid={`${testIdPrefix}-sub-submit-btn`}
              >
                {uploading ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Uploading...</>
                ) : (
                  <><Upload className="h-4 w-4 mr-2" />Add Submittal</>
                )}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => { resetForm(); setShowAddModal(false); }}
                data-testid={`${testIdPrefix}-sub-cancel-btn`}
              >
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Preview Modal */}
      <PreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        fileUrl={previewFile.url}
        filename={previewFile.name}
      />

      {/* Confirm Dialog */}
      <ConfirmDialog {...confirmState} onClose={() => setConfirmState({ open: false })} />
    </div>
  );
};
